import { AxiosRequestConfig } from 'axios';

import { actingRoles, clearSession, getSessionIdentity, identityOfAccess, Role, storeSession } from '../session';
import { RefreshUnavailableError } from './errors';
import axiosInstance, { RetriableConfig } from './instance';
import { LoginOutcome, LoginResult, MfaAbortedError, MfaPending, readChallenge } from './mfaChallenge';
import { refreshWithinLock, revokeCookie } from './refresh';
import { withSessionLock } from './sessionLock';
import { CSRF_HEADERS, LOGIN_ENDPOINT, sidOf } from './webSession';

/**
 * トークンを受け取った後の確かめ（錠の中で呼ぶ）。ログイン（200）と2段階認証の最後の段で同じ。
 *  - プロフィールの roles と access の act・amr から、このセッションで使える役割を求める（RV-040）
 *  - 使える役割（manager・operator）が無ければ後始末をして not_manager
 *  - Cookie の refresh で更新できることを確かめる（ADM-17）
 * 後始末は自分が発行したセッションだけを対象にする（印は自分のものなら消す、Cookie は sid を添えて失効）。
 */
export async function establishSessionWithinLock(access: string, signal?: AbortSignal): Promise<LoginOutcome> {
  const mine = identityOfAccess(access);
  if (signal?.aborted) {
    // 画面がこの試行をやめた。トークンを保存せず、発行された Cookie を失効させる。
    await revokeCookie(sidOf(mine));
    throw new MfaAbortedError();
  }
  storeSession(access);

  const discard = async () => {
    if (getSessionIdentity() === mine) clearSession();
    await revokeCookie(sidOf(mine));
  };

  // RV-040。役割をサーバーのプロフィールで確かめる。錠の中なので、401 でも更新しない
  // （更新は同じ錠を待つため）。
  let roles: Role[];
  try {
    const profile = await axiosInstance.get('/v1/profile/', {
      _retried: true,
    } as RetriableConfig);
    // YAC-2026-009 PR5a。役割は roles（配列）で読む。role（派生値）は読まない。
    roles = actingRoles(access, profile?.data?.roles);
  } catch (error) {
    await discard();
    throw error;
  }
  if (signal?.aborted) {
    await discard();
    throw new MfaAbortedError();
  }
  if (roles.length === 0) {
    await discard();
    return 'not_manager';
  }
  storeSession(access, roles);

  // ADM-17。移動先は再読み込みになり、access はメモリから消える。Cookie で取り直せる
  // ことをここで一度確かめる。管理画面と API が別サイト（例: 127.0.0.1 と localhost）
  // だと Cookie が保存されず、移動先からログイン画面へ戻り続けてしまうため。
  try {
    await refreshWithinLock(mine as string);
  } catch (error) {
    await discard();
    return error instanceof RefreshUnavailableError ? 'network' : 'cookie_unavailable';
  }
  if (signal?.aborted) {
    await discard();
    throw new MfaAbortedError();
  }
  return 'ok';
}

/**
 * ADM-17。管理画面のログイン。refresh はサーバーが HttpOnly Cookie で返し、access は
 * メモリに置く。
 *
 * ログイン・役割の確認（RV-040）・Cookie で更新できることの確認・失敗したときの後始末を、
 * まとめて1つの錠の中で行う（PR #90 レビュー 4176276491）。錠を /v1/web/token の応答で
 * 放すと、確認と後始末が錠の外で走り、2つのタブで同時にログインしたとき、先のタブの
 * 後始末が後のタブの成功したログインを消し得た。後始末は自分が発行したセッションだけを
 * 対象にする（印は自分のものなら消す、Cookie は sid を添えて失効させる）。
 *
 * keepSignedIn はログインの画面の「ログイン状態を保持する」（既定は外れ）。
 */
export function loginSession(
  email: string,
  password: string,
  keepSignedIn = false,
  config: AxiosRequestConfig = {}
): Promise<LoginResult> {
  return withSessionLock(async () => {
    // EV-491。離れた画面の更新の応答は、前のセッションの Path の Cookie しか書けない（待たない）。
    // BE-05（2026-10-14 PM 決定）。「ログイン状態を保持する」を送る。期限はサーバーが決める
    // （付ければ使うたびに1日延ばして最初のログインから30日まで、外せばログインから1日）。
    // 運営（2段階認証の後のセッション）には効かない（ログインから1日。YAC-2026-009 PR3）。
    const response = await axiosInstance.post(
      LOGIN_ENDPOINT,
      { email, password, keep_signed_in: keepSignedIn },
      { ...config, withCredentials: true, headers: { ...CSRF_HEADERS } }
    );
    // YAC-2026-009 PR5a。運営は 202 {mfa, mfa_token, expires_in}。トークンも Cookie も無い。
    if (response?.status === 202) {
      return { kind: 'mfa', challenge: readChallenge(response) } as MfaPending;
    }
    const access = response?.data?.access;
    if (!access) throw new Error('Login response did not contain an access token');
    return establishSessionWithinLock(access);
  });
}

/**
 * ADM-17 / ADM-23。Cookie の refresh を失効させる（サーバーが Cookie も消す）。
 * access は要らない。このタブのセッションの sid を添え、Cookie が別のログインのものなら
 * 失効させない。失効を確認できたら true、できなければ（400・409・通信断）false。
 */
export function logoutSession(): Promise<boolean> {
  const sid = sidOf(getSessionIdentity());
  // Web Locks が無い接続では送らない（失効は確認できない）。
  return withSessionLock(() => revokeCookie(sid)).catch(() => false);
}
